/**
 * Shell autenticado no padrão APROVADO na DEMO-03 (Casca do protótipo):
 * faixa de ambiente (ADR-006; a produção não tem faixa) + menu lateral claro
 * com o símbolo INTTRIX + trilha com links + conteúdo. Os módulos ainda não
 * portados para a aplicação funcional aparecem desabilitados ("em breve"),
 * sem antecipar escopo; cada item só aparece ativo com a permissão de ver.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icone, type NomeIcone } from './icones.tsx';
import { BuscaGlobal } from './BuscaGlobal.tsx';
import { ContextoTrilha } from './trilha.tsx';
import { useSessao } from '../sessao.tsx';
import { FaixaAmbiente, useAmbiente } from '../ambiente.tsx';
import { ABAS_ADMIN } from '../telas/Administracao.tsx';

/** Preferência do menu enquanto a página estiver aberta (memória, não armazenamento do navegador). */
let menuRecolhidoNaPagina = false;

export type Trilha = (string | { rotulo: string; rota?: string })[];

interface ItemMenu {
  rotulo: string;
  icone: NomeIcone;
  rota?: string;
  ativo?: RegExp;
  visivel?: boolean;
  emBreve?: string;
}

export function Shell({ rota, trilha, children }: { rota: string; trilha: Trilha; children: ReactNode }) {
  const { eu, sair, pode } = useSessao();
  // Nome do registro informado pela tela (vale só para a rota em que foi informado).
  const [rotuloFinal, setRotuloFinal] = useState<{ rota: string; textos: string[] } | null>(null);
  const definirRotulo = useCallback((textos: string[]) => setRotuloFinal({ rota, textos }), [rota]);
  const itensTrilha =
    rotuloFinal?.rota === rota && trilha.length >= rotuloFinal.textos.length
      ? [
          ...trilha.slice(0, trilha.length - rotuloFinal.textos.length),
          ...rotuloFinal.textos.map((t, i) => {
            const x = trilha[trilha.length - rotuloFinal.textos.length + i];
            return typeof x === 'string' ? t : { ...x, rotulo: t };
          }),
        ]
      : trilha;
  const { faixa } = useAmbiente();
  // Menu recolhido por escolha do usuário; nunca expande sozinho ao passar o mouse. Fica SÓ na memória da página
  // (vale ao navegar entre as telas; recarregar ou entrar de novo volta ao expandido): nada é guardado no navegador (CP11).
  const [recolhido, setRecolhidoEstado] = useState(() => menuRecolhidoNaPagina);
  const setRecolhido = (v: boolean) => ((menuRecolhidoNaPagina = v), setRecolhidoEstado(v));
  const podeAcesso = ['usuario', 'perfil'].some((e) => pode('visualizar', e));
  const cadastroInicial = ABAS_ADMIN.find((a) => a.grupo === 'cadastros' && pode('visualizar', a.entidade));
  const podeAuditoria = pode('visualizar', 'log_auditoria') || pode('visualizar', 'log_acesso');

  const menu: { titulo?: string; itens: ItemMenu[] }[] = [
    {
      itens: [
        { rotulo: 'Início', icone: 'inicio', rota: '/inicio', ativo: /^\/inicio/, visivel: pode('visualizar', 'oportunidade') || pode('visualizar', 'conta_pagar') || pode('visualizar', 'conta_receber') || pode('visualizar', 'conciliacao') },
        { rotulo: 'Tarefas', icone: 'tarefas', rota: '/tarefas', ativo: /^\/tarefas/, visivel: pode('visualizar', 'tarefa') },
      ],
    },
    {
      titulo: 'Comercial',
      itens: [
        { rotulo: 'Clientes', icone: 'clientes', rota: '/clientes', ativo: /^\/clientes/, visivel: pode('visualizar', 'cliente') },
        { rotulo: 'Contatos', icone: 'contatos', rota: '/contatos', ativo: /^\/contatos/, visivel: pode('visualizar', 'contato') },
        { rotulo: 'Oportunidades', icone: 'oportunidades', rota: '/oportunidades', ativo: /^\/oportunidades/, visivel: pode('visualizar', 'oportunidade') },
        { rotulo: 'Pedidos', icone: 'pedidos', rota: '/pedidos', ativo: /^\/pedidos/, visivel: pode('visualizar', 'pedido') },
        { rotulo: 'Ordens', icone: 'ordens', rota: '/ordens', ativo: /^\/ordens/, visivel: pode('visualizar', 'ordem') },
      ],
    },
    {
      titulo: 'Financeiro',
      itens: [
        { rotulo: 'Contas a Receber', icone: 'receber', rota: '/contas-receber', ativo: /^\/contas-receber/, visivel: pode('visualizar', 'conta_receber') },
        // Financeiro A2: Contas a Pagar ativo para quem pode ver (conciliação, recorrência, cartão e reembolso: etapas próprias)
        { rotulo: 'Contas a Pagar', icone: 'pagar', rota: '/contas-pagar', ativo: /^\/contas-pagar/, visivel: pode('visualizar', 'conta_pagar') },
        // Conciliação B1: item próprio do menu Financeiro (D3), para quem pode ver
        { rotulo: 'Conciliação bancária', icone: 'moedas', rota: '/conciliacao', ativo: /^\/conciliacao/, visivel: pode('visualizar', 'conciliacao') },
        // E1 (D13): Aplicações como item próprio do Financeiro (ver = visualizar:conciliacao)
        { rotulo: 'Aplicações', icone: 'dinheiro', rota: '/aplicacoes', ativo: /^\/aplicacoes/, visivel: pode('visualizar', 'conciliacao') },
        // E2–E4: Fluxo de caixa (realizado, projetado e fotos) — cada parte respeita a permissão da sua origem
        { rotulo: 'Fluxo de caixa', icone: 'caixa', rota: '/fluxo-caixa', ativo: /^\/fluxo-caixa/, visivel: pode('visualizar', 'conta_pagar') || pode('visualizar', 'conta_receber') || pode('visualizar', 'conciliacao') },
      ],
    },
    {
      titulo: 'Administração · Cadastros base',
      itens: [
        { rotulo: 'Parceiros comerciais', icone: 'parceiros', rota: '/administracao/parceiros', ativo: /^\/administracao\/(parceiros|fornecedores)/, visivel: pode('visualizar', 'parceiro') },
        { rotulo: 'Produtos e Serviços', icone: 'produtos', rota: '/administracao/produtos', ativo: /^\/administracao\/produtos/, visivel: pode('visualizar', 'produto') },
        { rotulo: 'Parâmetros e auditoria', icone: 'parametros', rota: '/administracao/parametros-comerciais', ativo: /^\/administracao\/parametros-comerciais/, visivel: pode('visualizar', 'parametro') },
      ],
    },
    {
      titulo: 'Administração',
      itens: [
        { rotulo: 'Usuários e Permissões', icone: 'usuarios', rota: pode('visualizar', 'usuario') ? '/administracao/usuarios' : '/administracao/perfis', ativo: /^\/administracao\/(usuarios|perfis)/, visivel: podeAcesso },
        { rotulo: 'Cadastros base', icone: 'cadastros', rota: `/administracao/${cadastroInicial?.rota ?? 'parametros'}`, ativo: /^\/administracao\/(parametros|plano-contas|centros-custo|contas-bancarias|cartoes|pessoas-financeiras)(\/|$)/, visivel: Boolean(cadastroInicial) },
        { rotulo: 'Auditoria', icone: 'auditoria', rota: '/auditoria', ativo: /^\/auditoria/, visivel: podeAuditoria },
        // L6: Administração › Lixeira — para quem tem "Ver na Lixeira" em alguma entidade
        { rotulo: 'Lixeira', icone: 'lixeira', rota: '/lixeira', ativo: /^\/lixeira/, visivel: ['oportunidade', 'proposta', 'pedido', 'ordem'].some((e) => pode('ver_lixeira', e)) },
      ],
    },
  ];

  return (
    <>
      <a
        className="pular"
        href="#conteudo"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('conteudo')?.focus();
        }}
      >
        Pular para o conteúdo
      </a>
      <FaixaAmbiente />
      <div className={`shell shell-hub${faixa ? '' : ' sem-faixa'}${recolhido ? ' menu-recolhido' : ''}`}>
        <aside className="lateral">
          <div className="marca-menu-linha">
            <a className="marca marca-compacta-menu" href="#/" aria-label="INTTRIX HUB — Início" data-teste="marca">
              <img src="marca/simbolo-inttrix.jpg" alt="Símbolo INTTRIX" width="36" height="38" />
              <span className="nome-produto">
                INTTRIX <strong>HUB</strong>
              </span>
            </a>
            <button type="button" className="recolher-menu" onClick={() => setRecolhido(!recolhido)} aria-label={recolhido ? 'Expandir menu' : 'Recolher menu'} aria-expanded={!recolhido} title={recolhido ? 'Expandir menu' : 'Recolher menu'} data-teste="recolher-menu">
              <Icone nome={recolhido ? 'expandir' : 'recolher'} />
            </button>
          </div>
          <nav aria-label="Navegação principal" data-teste="menu">
            {menu.map((g, i) => {
              const itens = g.itens.filter((it) => it.visivel !== false);
              if (!itens.length) return null;
              return (
                <div key={i} className="menu-grupo">
                  {g.titulo && <div className="menu-titulo">{g.titulo}</div>}
                  {itens.map((it) =>
                    it.rota ? (
                      <a key={it.rotulo} className={`menu-item${it.ativo?.test(rota) ? ' ativo' : ''}`} href={`#${it.rota}`} aria-current={it.ativo?.test(rota) ? 'page' : undefined} aria-label={recolhido ? it.rotulo : undefined} data-dica={it.rotulo}>
                        <Icone nome={it.icone} />
                        <span className="menu-rotulo">{it.rotulo}</span>
                      </a>
                    ) : (
                      <span key={it.rotulo} className="menu-item desabilitado" title={it.emBreve} aria-disabled="true" aria-label={recolhido ? `${it.rotulo} (em breve)` : undefined} data-dica={`${it.rotulo} · em breve`} tabIndex={recolhido ? 0 : undefined}>
                        <Icone nome={it.icone} />
                        <span className="menu-rotulo">{it.rotulo}</span> <small>em breve</small>
                      </span>
                    ),
                  )}
                </div>
              );
            })}
          </nav>
        </aside>
        <div className="principal">
          <header className="topo">
            <nav className="trilha" aria-label="Você está em">
              {itensTrilha.map((t, i) => {
                const x = typeof t === 'string' ? { rotulo: t } : t;
                return (
                  <span key={i}>
                    {i > 0 && <span className="sep">›</span>}
                    {x.rota && i < itensTrilha.length - 1 ? <a href={`#${x.rota}`}>{x.rotulo}</a> : x.rotulo}
                  </span>
                );
              })}
            </nav>
            <BuscaGlobal />
            <MenuUsuario nome={eu?.nome ?? ''} perfil={eu?.perfil_nome ?? ''} sair={sair} />
          </header>
          <main className="conteudo" id="conteudo" tabIndex={-1}>
            <ContextoTrilha.Provider value={definirRotulo}>{children}</ContextoTrilha.Provider>
          </main>
        </div>
      </div>
    </>
  );
}

/** Iniciais do nome (primeiro e último nome): "Valéria Frazão" → "VF". */
export const iniciais = (nome: string) => {
  const p = nome.trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '?';
};

/**
 * P2-17: no topo só as iniciais; ao clicar abre nome completo, perfil, "Dados do usuário" (Minha conta) e "Sair".
 * Mesmas funções de antes, sem novo fluxo de autenticação. Teclado: Enter/Espaço abre, Esc fecha e devolve o foco.
 */
function MenuUsuario({ nome, perfil, sair }: { nome: string; perfil: string; sair: () => void }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setAberto(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && (setAberto(false), botao.current?.focus());
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => (document.removeEventListener('mousedown', fora), document.removeEventListener('keydown', esc));
  }, [aberto]);
  return (
    <div className="usuario-topo menu-usuario" ref={ref}>
      <button ref={botao} type="button" className="avatar-usuario" aria-haspopup="menu" aria-expanded={aberto} aria-label={`${nome} — menu do usuário`} title={nome} onClick={() => setAberto(!aberto)} data-teste="menu-usuario">
        <span className="bola" aria-hidden="true">
          {iniciais(nome)}
        </span>
        <span aria-hidden="true">▾</span>
      </button>
      {aberto && (
        <div className="painel-usuario" role="menu" aria-label="Menu do usuário" data-teste="painel-usuario">
          <div className="quem">
            <b data-teste="nome-usuario">{nome}</b>
            <span className="selo selo-destaque">{perfil}</span>
          </div>
          <a href="#/minha-conta" role="menuitem" className="item-menu-usuario" onClick={() => setAberto(false)} data-teste="dados-usuario">
            <Icone nome="usuario" /> Dados do usuário
          </a>
          <button type="button" role="menuitem" className="item-menu-usuario" onClick={() => sair()} data-teste="sair">
            <Icone nome="sair" /> Sair
          </button>
        </div>
      )}
    </div>
  );
}
